// Regions of interest on a condition, normalized to 0..1 of the image. Mirrors
// 3d-backend/services/spatial-vision/roi.js, which validates again on save.
const ROUND = 1e4;

const unit = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0 || numeric > 1) return null;
  return Math.round(numeric * ROUND) / ROUND;
};

const clamp01 = value => Math.min(1, Math.max(0, Number(value) || 0));

// A region keeps what is inside it; mode "outside" throws away what is inside it
// instead. Inside is the default and is not stored.
const withMode = (shape, input) =>
  input.mode === 'outside' ? { ...shape, mode: 'outside' } : shape;

function normalizeShape(input) {
  if (input.type === 'rect') {
    const x = unit(input.x);
    const y = unit(input.y);
    const w = unit(input.w);
    const h = unit(input.h);
    if ([x, y, w, h].includes(null) || w <= 0 || h <= 0) return null;
    if (x + w > 1 || y + h > 1) return null;
    return { type: 'rect', x, y, w, h };
  }
  if (input.type === 'circle') {
    const cx = unit(input.cx);
    const cy = unit(input.cy);
    const r = unit(input.r);
    if ([cx, cy, r].includes(null) || r <= 0) return null;
    return { type: 'circle', cx, cy, r };
  }
  if (input.type === 'polygon' && Array.isArray(input.points)) {
    const points = input.points.map(point =>
      Array.isArray(point) ? [unit(point[0]), unit(point[1])] : [null, null],
    );
    if (points.length < 3 || points.some(point => point.includes(null))) {
      return null;
    }
    return { type: 'polygon', points };
  }
  return null;
}

const round = value => Math.round(value * ROUND) / ROUND;
const between = (value, min, max) => Math.min(max, Math.max(min, value));

// A drag moves a shape whole and never resizes it: the radius, the width, the height
// and the vertices are copied untouched. The result is held inside 0..1 because
// normalizeRoi answers null for a rect that runs past an edge, which would delete the
// mark being dragged; a rect and a circle clamp the moved coordinate, a polygon clamps
// the delta so the ring keeps its shape. `aspect` is width / height of the frame the
// shape is drawn on, which only the caller knows; a circle needs it because its radius
// is a fraction of the width and covers a different share of the height.
export function translateRoi(shape, dx, dy, aspect = 1) {
  if (!shape) return null;
  if (shape.type === 'rect') {
    return {
      ...shape,
      x: round(between(shape.x + dx, 0, 1 - shape.w)),
      y: round(between(shape.y + dy, 0, 1 - shape.h)),
    };
  }
  if (shape.type === 'circle') {
    // Clamping the centre to 0..1 alone lets three quarters of the circle hang off
    // the corner, and normalizeRoi accepts that: it checks cx, cy and r for being
    // inside 0..1 and never the body. A circle too big for the frame has nowhere
    // to sit but the middle.
    const halfWidth = Math.min(shape.r, 0.5);
    const halfHeight = Math.min(shape.r * (Number(aspect) || 1), 0.5);
    return {
      ...shape,
      cx: round(between(shape.cx + dx, halfWidth, 1 - halfWidth)),
      cy: round(between(shape.cy + dy, halfHeight, 1 - halfHeight)),
    };
  }
  if (shape.type === 'polygon' && Array.isArray(shape.points)) {
    const xs = shape.points.map(point => point[0]);
    const ys = shape.points.map(point => point[1]);
    const stepX = between(dx, -Math.min(...xs), 1 - Math.max(...xs));
    const stepY = between(dy, -Math.min(...ys), 1 - Math.max(...ys));
    return {
      ...shape,
      points: shape.points.map(([x, y]) => [
        round(x + stepX),
        round(y + stepY),
      ]),
    };
  }
  return shape;
}

export function normalizeRoi(input) {
  if (!input || typeof input !== 'object') return null;
  const shape = normalizeShape(input);
  return shape ? withMode(shape, input) : null;
}

// A condition holds a list of regions; a single shape from an older row is wrapped.
export function normalizeRois(input) {
  if (!input) return null;
  const shapes = (Array.isArray(input) ? input : [input])
    .map(normalizeRoi)
    .filter(Boolean);
  return shapes.length ? shapes : null;
}

export function rectFromDrag(start, end) {
  const x1 = clamp01(start.x);
  const y1 = clamp01(start.y);
  const x2 = clamp01(end.x);
  const y2 = clamp01(end.y);
  return normalizeRoi({
    type: 'rect',
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    w: Math.abs(x2 - x1),
    h: Math.abs(y2 - y1),
  });
}

// The radius is stored relative to the image width, so a vertical drag has to be
// scaled by the aspect ratio before it means the same distance as a horizontal one.
export function circleFromDrag(center, edge, aspect) {
  const dx = edge.x - center.x;
  const dy = (edge.y - center.y) / aspect;
  return normalizeRoi({
    type: 'circle',
    cx: clamp01(center.x),
    cy: clamp01(center.y),
    r: Math.min(1, Math.hypot(dx, dy)),
  });
}

export function isNearFirstPoint(first, point, imageWidth, tolerancePx) {
  return (
    Math.hypot(point.x - first.x, point.y - first.y) * imageWidth <= tolerancePx
  );
}

export function roiToSvg(roi, imageWidth, imageHeight) {
  if (!roi) return null;
  if (roi.type === 'rect') {
    return {
      tag: 'rect',
      attrs: {
        x: roi.x * imageWidth,
        y: roi.y * imageHeight,
        width: roi.w * imageWidth,
        height: roi.h * imageHeight,
      },
    };
  }
  if (roi.type === 'circle') {
    return {
      tag: 'circle',
      attrs: {
        cx: roi.cx * imageWidth,
        cy: roi.cy * imageHeight,
        r: roi.r * imageWidth,
      },
    };
  }
  if (roi.type === 'polygon') {
    return {
      tag: 'polygon',
      attrs: {
        points: roi.points
          .map(([x, y]) => `${x * imageWidth},${y * imageHeight}`)
          .join(' '),
      },
    };
  }
  return null;
}

const percent = value => `${Math.round(value * 100)}%`;

export function roiSummary(roi) {
  if (!roi) return 'Whole image';
  const suffix = roi.mode === 'outside' ? ', outside' : '';
  if (roi.type === 'rect')
    return `Rectangle ${percent(roi.w)} × ${percent(roi.h)}${suffix}`;
  if (roi.type === 'circle') return `Circle r ${percent(roi.r)}${suffix}`;
  if (roi.type === 'polygon')
    return `Polygon ${roi.points.length} points${suffix}`;
  return 'Whole image';
}

export function roisSummary(rois) {
  if (!rois?.length) return 'Whole image';
  if (rois.length === 1) return roiSummary(rois[0]);
  return `${rois.length} regions`;
}
